/***
 *
 *   SEARCH
 *   Search input field. Ported from
 *   ui-reference/src/components/ui/search-input.tsx — a leading magnifier
 *   inside the field, with an optional clear affordance. The previous
 *   implementation used a trailing submit button; the reference has none,
 *   because search is always live.
 *
 *   PROPS
 *   throttle: throttle the callback execution in ms
 *   callback: function executed on change
 *   placeholder: override the default
 *
 **********/

import { useState, useEffect } from 'react';
import { Icon, ClassHelper } from 'components/lib';

import Style from './search.tailwind.js';
import { useTranslate } from 'app/translations.js';

export function Search(props) {
    const [value, setValue] = useState(props.value || '');
    const [typing, setTyping] = useState(false);
    const { _t } = useTranslate();

    useEffect(() => {
        // throttle typing
        if (props.throttle && !typing) {
            const onKeyPress = () => {
                setTyping(true);
                setTimeout(() => {
                    setTyping(false);
                }, props.throttle);
            };

            window.addEventListener('keydown', onKeyPress);
            return () => window.removeEventListener('keydown', onKeyPress);
        }
    }, [props.throttle, typing]);

    useEffect(() => {
        // callback when typing throttle done
        if (props.throttle && !typing) props.callback(value);
    }, [props, typing, value]);

    const searchStyle = ClassHelper(Style, {
        base: true,
        className: props.className,
    });

    return (
        <div className={searchStyle}>
            <Icon
                image="search"
                size={16}
                color="currentColor"
                className={Style.icon}
            />

            <input
                type="text"
                className={Style.input}
                placeholder={props.placeholder || _t('search')}
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    !props.throttle && props.callback(e.target.value);
                }}
            />

            {value && (
                <span
                    className={Style.clear}
                    role="button"
                    tabIndex={0}
                    aria-label={_t('cancel')}
                    onClick={() => {
                        setValue('');
                        props.callback('');
                    }}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        setValue('');
                        props.callback('');
                    }}>
                    <Icon image="x" size={14} color="currentColor" />
                </span>
            )}
        </div>
    );
}
